import axiosInstance from "../../utility/axios";

import {
  deleteService,
  setLoading,
  createService,
  setServices,
  setTotal,
  editService,
  setService,
  setLoadingServiceId,
  setPackages,
  addPackage,
  updatePackage,
} from "../../store/slices/services";
import { AppDispatch } from "../../store/store";
import {
  ICreateService,
  IEditService,
  IService,
  IServiceAnalyticData,
  IServicePackage,
  IServiceResponseData,
} from "../../store/types/service";
import { toast } from "sonner";
import { IApiResponse } from "@/src/store/types/api-response";
import { getErrorMessage } from "../apiError";

const serviceEndpoint = "/product/api/v1/services";

export const get_services = async (
  dispatch: AppDispatch,
  limit: number,
  offset: number,
) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.get<
      IApiResponse<IServiceResponseData>
    >(`${serviceEndpoint}`, { params: { limit, offset } });
    dispatch(setServices(response.data));
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to load services"));
  } finally {
    dispatch(setLoading(false));
  }
};

export const get_service = async (
  dispatch: AppDispatch,
  id: string | number,
) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.get<IApiResponse<IService>>(
      `${serviceEndpoint}/${id}`,
    );

    dispatch(setService(response.data.data));
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to load service"));
  } finally {
    dispatch(setLoading(false));
  }
};

export const create_service = async (
  form: ICreateService,
  dispatch: AppDispatch,
): Promise<IService | null> => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.post<IApiResponse<IService>>(
      serviceEndpoint,
      { ...form, packages: [] },
    );
    const { message, data } = response.data;
    toast.success(message || "Service created successfully");
    dispatch(createService(data));
    return data;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to create service"));
    return null;
  } finally {
    dispatch(setLoading(false));
  }
};

export const edit_service = async (
  form: IEditService,
  dispatch: AppDispatch,
) => {
  dispatch(setLoading(true));
  try {
    const { id, country_id, name, image, description, price } = form;
    const response = await axiosInstance.patch<IApiResponse<IService>>(
      `${serviceEndpoint}/${id}`,
      { country_id, name, image, description, price },
    );

    const { data, message } = response.data;
    toast.success(message || "Service updated successfully");
    dispatch(editService(data));
    dispatch(setService(data));
    return true;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to update service"));
    return false;
  } finally {
    dispatch(setLoading(false));
  }
};

export const delete_service = async (dispatch: AppDispatch, id: number) => {
  dispatch(setLoadingServiceId(id));
  try {
    const response = await axiosInstance.delete<IApiResponse<null>>(
      `${serviceEndpoint}/${id}`,
    );
    const { message } = response.data;
    dispatch(deleteService(Number(id)));
    toast.success(message || "Service deleted successfully");
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to delete service"));
  } finally {
    dispatch(setLoadingServiceId(null));
  }
};

// ================================================================================

export const get_total_service = async (
  dispatch: AppDispatch,
): Promise<void> => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.get<
      IApiResponse<IServiceAnalyticData>
    >(`${serviceEndpoint}/analytics`);
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to load service total");
    }
    dispatch(setTotal(response.data.data));
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load service total"));
  } finally {
    dispatch(setLoading(false));
  }
};

export const update_service_status = async (
  dispatch: AppDispatch,
  id: string | number,
) => {
  try {
    const response = await axiosInstance.patch<IApiResponse<IService>>(
      `${serviceEndpoint}/${id}/status`,
    );

    const { data, message } = response.data;
    dispatch(editService(data));
    toast.success(message || "Service status updated successfully");
    return true;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to update service status"));
    return false;
  }
};

//=============================================================================
//
//      Packages Api Services
//
//
//=============================================================================

export const get_packages = async (
  service_id: number,
  dispatch: AppDispatch,
): Promise<void> => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.get<IApiResponse<IServicePackage[]>>(
      `${serviceEndpoint}/${service_id}/packages`,
    );
    dispatch(setPackages(response.data.data));
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to get package"));
  } finally {
    dispatch(setLoading(false));
  }
};

export const create_packages = async (
  dispatch: AppDispatch,
  service_id: number,
  name: string,
): Promise<IServicePackage | null> => {
  try {
    const response = await axiosInstance.post<IApiResponse<IServicePackage>>(
      `${serviceEndpoint}/${service_id}/packages`,
      { name },
    );
    dispatch(
      addPackage({
        serviceId: service_id,
        package: response.data.data,
      }),
    );
    toast.success(response.data.message || "Package created successfully");
    return response.data.data;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to create package"));
    return null;
  }
};

export const update_package = async (
  dispatch: AppDispatch,
  service_id: number,
  package_id: number,
  name: string,
): Promise<IServicePackage | null> => {
  try {
    const response = await axiosInstance.patch<IApiResponse<IServicePackage>>(
      `${serviceEndpoint}/${service_id}/packages/${package_id}`,
      { name },
    );
    dispatch(
      updatePackage({
        serviceId: service_id,
        package: response.data.data,
      }),
    );
    toast.success(response.data.message || "Package updated successfully");
    return response.data.data;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to update package"));
    return null;
  }
};

export const delete_package = async (
  dispatch: AppDispatch,
  service_id: number,
  package_id: number,
): Promise<boolean> => {
  dispatch(setLoadingServiceId(service_id));
  try {
    const response = await axiosInstance.delete<IApiResponse<IService>>(
      `${serviceEndpoint}/${service_id}/packages/${package_id}`,
    );

    const { message, data } = response.data;
    dispatch(editService(data));
    dispatch(setService(data));
    toast.success(message || "Package deleted successfully");
    return true;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to delete package"));
    return false;
  } finally {
    dispatch(setLoadingServiceId(null));
  }
};
